/**
 * Tracé SVG d'un circuit en ovale — vue de dessus, style Mario Kart.
 *
 * Props :
 *   nbCases   — nombre total de cases (10–50)
 *   nbBonus   — nombre de cases bonus (sections dorées)
 *   karts     — [{ id, type_vehicule, position_case, vitesse, effet_actif, effet_expire_le }]
 *   kartSize  — taille des emojis kart en unités SVG (défaut 16)
 *   myKartId  — id du kart du joueur courant (encadré en rouge)
 *   pieges    — [position_case] des bananes déposées sur le circuit
 *   className — classes Tailwind supplémentaires
 */
import { useEffect, useId, useRef, useState } from 'react'
import { getTwemojiUrl } from '../../lib/twemoji.jsx'

function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2
}

// Convertit un score de progression (potentiellement décimal) en coordonnées SVG
function scoreToXY(score, nbCases, cx, rx_c, cy, ry_c) {
  const posCase = ((score % nbCases) + nbCases) % nbCases
  const t = (2 * Math.PI * posCase / nbCases) - Math.PI / 2
  return { x: cx + rx_c * Math.cos(t), y: cy + ry_c * Math.sin(t) }
}

// Emoji Twemoji statique (image <image> dans le SVG)
function svgEmoji(emoji, x, y, size) {
  const url = getTwemojiUrl(emoji)
  return <image key={`${emoji}-${x}-${y}`} href={url} x={x - size / 2} y={y - size / 2} width={size} height={size} />
}

// Retourne l'emoji d'effet actif si encore valide
function activeEffectEmoji(kart) {
  if (!kart.effet_actif || !kart.effet_expire_le) return null
  if (new Date(kart.effet_expire_le) <= new Date()) return null
  const map = { shield: '🛡', star: '⭐', freeze: '❄️' }
  return map[kart.effet_actif] ?? null
}

export default function CircuitTrack({
  nbCases = 20,
  nbBonus = 3,
  karts = [],
  ghostKarts = [],
  kartSize = 16,
  myKartId = null,
  pieges = [],
  className = '',
  animatedViewBox = null,
  projectile = null,
  pseudoTarget = null,
  attackEvents = [],
  autoPan = false,
  imageUrl = null,
}) {
  const uid = useId().replace(/:/g, '')
  const filterId = `bs-${uid}`

  const W = 440, H = 250
  const cx = W / 2, cy = 133

  const rx_out = W * 0.90 / 2
  const ry_out = H * 0.78 / 2
  const rx_in  = W * 0.70 / 2
  const ry_in  = H * 0.58 / 2

  const rx_c = (rx_out + rx_in) / 2
  const ry_c = (ry_out + ry_in) / 2
  const deltaRx = rx_out - rx_in
  const deltaRy = ry_out - ry_in

  // ── Animation de déplacement des karts ─────────────────────────────────────
  // prevKartsRef : snapshot { kartId → score } de la position précédente
  // animStateRef : { kartId → { fromScore, toScore, startTime, duration } }
  // animatedPositions : { kartId → { x, y } } — positions interpolées courantes
  const prevKartsRef   = useRef({})
  const animStateRef   = useRef({})
  const rafRef         = useRef(null)
  const [animatedPositions, setAnimatedPositions] = useState({})

  // ── Animations de projectiles d'attaque ────────────────────────────────────
  // processedAttackIdsRef : Set des IDs d'attaques déjà traités (évite les doublons)
  // attackAnimRef : [{ id, emoji, fromScore, toScore, startTime, toKartId }]
  // activeProjectiles : [{ id, emoji, x, y }] — positions courantes pour le rendu
  const ATTACK_TRAVEL_MS = 2000   // durée du déplacement attaquant → cible
  const ATTACK_DWELL_MS  = 1500   // durée d'affichage à la cible avant disparition
  const processedAttackIdsRef = useRef(new Set())
  const attackAnimRef         = useRef([])
  const [activeProjectiles, setActiveProjectiles] = useState([])
  const [targetedKartIds, setTargetedKartIds]     = useState(new Set())
  const [hoveredKartId, setHoveredKartId]         = useState(null)

  // Boucle RAF : interpole les positions karts + projectiles d'attaque
  function tick(now) {
    // ── Karts ──
    const newPositions = {}
    let stillAnimating = false
    const toDelete = []

    Object.entries(animStateRef.current).forEach(([kartId, anim]) => {
      const elapsed  = now - anim.startTime
      const progress = Math.min(elapsed / anim.duration, 1)
      const eased    = easeInOut(progress)
      const score    = anim.fromScore + (anim.toScore - anim.fromScore) * eased
      newPositions[kartId] = scoreToXY(score, nbCases, cx, rx_c, cy, ry_c)
      if (progress < 1) {
        stillAnimating = true
      } else {
        toDelete.push(kartId)
      }
    })

    toDelete.forEach(id => { delete animStateRef.current[id] })
    setAnimatedPositions(newPositions)

    // ── Projectiles d'attaque ──
    const updatedProjectiles = []
    const stillActiveTargets = new Set()
    attackAnimRef.current = attackAnimRef.current.filter(anim => {
      const elapsed = now - anim.startTime
      // Expirée : retirer du tableau et ne pas inclure dans le rendu
      if (elapsed >= ATTACK_TRAVEL_MS + ATTACK_DWELL_MS) return false
      const travelProgress = Math.min(elapsed / ATTACK_TRAVEL_MS, 1)
      // Suit le tracé de l'ovale : interpolation du score, pas des XY
      const interpolatedScore = anim.fromScore + (anim.toScore - anim.fromScore) * travelProgress
      const pos = scoreToXY(interpolatedScore, nbCases, cx, rx_c, cy, ry_c)
      updatedProjectiles.push({ id: anim.id, emoji: anim.emoji, x: pos.x, y: pos.y })
      if (travelProgress < 1) stillActiveTargets.add(anim.toKartId)
      return true
    })
    setActiveProjectiles(updatedProjectiles)
    setTargetedKartIds(stillActiveTargets)

    if (stillAnimating || attackAnimRef.current.length > 0) {
      rafRef.current = requestAnimationFrame(tick)
    } else {
      rafRef.current = null
      setAnimatedPositions({})
      setActiveProjectiles([])
      setTargetedKartIds(new Set())
    }
  }

  // Détecte les déplacements et lance les animations (karts réels + fantômes)
  useEffect(() => {
    let started = false
    ;[...karts, ...ghostKarts].forEach(k => {
      const score = (k.tour_actuel - 1) * nbCases + k.position_case
      const prev  = prevKartsRef.current[k.id]
      if (prev !== undefined && prev !== score) {
        animStateRef.current[k.id] = {
          fromScore: prev,
          toScore:   score,
          startTime: performance.now(),
          duration:  350,
        }
        started = true
      }
      prevKartsRef.current[k.id] = score
    })
    if (started && !rafRef.current) {
      rafRef.current = requestAnimationFrame(tick)
    }
  }, [karts, ghostKarts, nbCases])

  // Nettoyage
  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
  }, [])

  // ── autoPan : dimensions du conteneur SVG (ResizeObserver) ────────────────
  const svgRef = useRef(null)
  const [containerSize, setContainerSize] = useState({ w: W, h: H })

  useEffect(() => {
    if (!autoPan || !svgRef.current) return
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      if (height > 0) setContainerSize({ w: width, h: height })
    })
    ro.observe(svgRef.current)
    return () => ro.disconnect()
  }, [autoPan])

  // ── autoPan : camera lag (RAF indépendant, lerp vers targetPanX) ──────────
  const targetPanXRef = useRef(0)
  const [smoothPanX, setSmoothPanX] = useState(0)

  useEffect(() => {
    if (!autoPan) return
    let currentPan = null
    let rafId
    const tick = () => {
      const target = targetPanXRef.current
      if (currentPan === null) currentPan = target
      currentPan += (target - currentPan) * 0.1
      setSmoothPanX(currentPan)
      rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [autoPan])
  // ───────────────────────────────────────────────────────────────────────────

  // Traitement des nouveaux événements d'attaque
  useEffect(() => {
    if (attackEvents.length === 0) return
    let added = false
    attackEvents.forEach(ev => {
      if (processedAttackIdsRef.current.has(ev.id)) return
      processedAttackIdsRef.current.add(ev.id)
      const fromKart = karts.find(k => k.id === ev.fromKartId)
      const toKart   = karts.find(k => k.id === ev.toKartId)
      if (!fromKart || !toKart) return
      const fromScore = (fromKart.tour_actuel - 1) * nbCases + fromKart.position_case
      const toScore   = (toKart.tour_actuel   - 1) * nbCases + toKart.position_case
      attackAnimRef.current.push({
        id:        ev.id,
        emoji:     ev.emoji,
        fromScore,
        toScore,
        toKartId:  ev.toKartId,
        startTime: performance.now(),
      })
      added = true
    })
    if (added && !rafRef.current) {
      rafRef.current = requestAnimationFrame(tick)
    }
  }, [attackEvents, karts, nbCases])
  // ───────────────────────────────────────────────────────────────────────────

  // ── autoPan : calcul viewBox dynamique ────────────────────────────────────
  const viewBoxWidth = autoPan && containerSize.h > 0
    ? Math.min(W, (containerSize.w / containerSize.h) * H)
    : W
  const maxPanX = Math.max(0, W - viewBoxWidth)
  const myKart = karts.find(k => k.id === myKartId)
  const myStaticX = myKart
    ? scoreToXY((myKart.tour_actuel - 1) * nbCases + myKart.position_case, nbCases, cx, rx_c, cy, ry_c).x
    : cx
  const playerX = autoPan && myKartId
    ? (animatedPositions[myKartId]?.x ?? myStaticX)
    : cx
  if (autoPan) {
    targetPanXRef.current = Math.max(0, Math.min(maxPanX, playerX - viewBoxWidth / 2))
  }
  const computedViewBox = autoPan
    ? `${smoothPanX.toFixed(1)} 0 ${viewBoxWidth.toFixed(1)} ${H}`
    : null

  const cells = Array.from({ length: nbCases }, (_, i) => {
    const t = (2 * Math.PI * i / nbCases) - Math.PI / 2
    return { x: cx + rx_c * Math.cos(t), y: cy + ry_c * Math.sin(t), t }
  })

  const effectiveBonus = Math.min(nbBonus, nbCases - 1)
  const bonusStep = effectiveBonus > 0 ? Math.floor((nbCases - 1) / (effectiveBonus + 1)) : 0
  const bonusIndices = new Set(
    Array.from({ length: effectiveBonus }, (_, i) => {
      const idx = 1 + bonusStep * (i + 1)
      return idx < nbCases ? idx : null
    }).filter(Boolean)
  )

  // Damier départ/arrivée
  const { t: startT, x: startX, y: startY } = cells[0]
  const normLen = Math.sqrt((ry_c * Math.cos(startT)) ** 2 + (rx_c * Math.sin(startT)) ** 2)
  const normX = (ry_c * Math.cos(startT)) / normLen
  const normY = (rx_c * Math.sin(startT)) / normLen
  const lineHalf = Math.sqrt(
    ((deltaRx / 2) * Math.cos(startT)) ** 2 +
    ((deltaRy / 2) * Math.sin(startT)) ** 2
  )
  const startAngleDeg = Math.atan2(normY, normX) * 180 / Math.PI
  const bandH  = 9
  const sqCols = 8
  const sqW    = (lineHalf * 2) / sqCols
  const sqH    = bandH / 2

  // Groupement des karts par case
  const kartsByCell = {}
  karts.forEach(k => {
    const pos = (k.position_case ?? 0) % nbCases
    if (!kartsByCell[pos]) kartsByCell[pos] = []
    kartsByCell[pos].push(k)
  })

  const floatUp = kartSize * 0.65
  const bubbleR = kartSize / 2 + 4.5
  const kartElements = []
  const kartBadges   = []

  Object.entries(kartsByCell).forEach(([posStr, group]) => {
    const { x: cellX, y: cellY, t } = cells[Number(posStr)]

    const tanRaw = Math.sqrt((rx_c * Math.sin(t)) ** 2 + (ry_c * Math.cos(t)) ** 2)
    const tanX = (-rx_c * Math.sin(t)) / tanRaw
    const tanY = (ry_c * Math.cos(t)) / tanRaw

    const overlapOffset = bubbleR * 2 * 0.4
    const visible = group.slice(0, 3)
    const extra   = group.length - 3

    visible.forEach((kart, i) => {
      const fan = visible.length > 1 ? (i - (visible.length - 1) / 2) * overlapOffset : 0
      kartElements.push({
        kart,
        kx: cellX + tanX * fan,
        ky: cellY - floatUp + tanY * fan,
      })
    })

    if (extra > 0) {
      const lastFan  = ((visible.length - 1) - (visible.length - 1) / 2) * overlapOffset
      const badgeFan = lastFan + overlapOffset
      kartBadges.push({
        n:  extra,
        bx: cellX + tanX * badgeFan,
        by: cellY - floatUp + tanY * badgeFan,
      })
    }
  })

  // Kart du joueur courant rendu en dernier (au-dessus des autres)
  kartElements.sort((a, b) => {
    if (a.kart.id === myKartId) return 1
    if (b.kart.id === myKartId) return -1
    return 0
  })

  // Chemin SVG de la piste (donut)
  const outerPath =
    `M ${cx + rx_out} ${cy} A ${rx_out} ${ry_out} 0 1 0 ${cx - rx_out} ${cy} ` +
    `A ${rx_out} ${ry_out} 0 1 0 ${cx + rx_out} ${cy} Z`
  const innerPath =
    `M ${cx + rx_in} ${cy} A ${rx_in} ${ry_in} 0 1 1 ${cx - rx_in} ${cy} ` +
    `A ${rx_in} ${ry_in} 0 1 1 ${cx + rx_in} ${cy} Z`

  function bonusSlicePath(t) {
    const halfArc = (Math.PI * 0.85) / nbCases
    const t1 = t - halfArc, t2 = t + halfArc
    const ox1 = cx + rx_out * Math.cos(t1), oy1 = cy + ry_out * Math.sin(t1)
    const ox2 = cx + rx_out * Math.cos(t2), oy2 = cy + ry_out * Math.sin(t2)
    const ix2 = cx + rx_in  * Math.cos(t1), iy2 = cy + ry_in  * Math.sin(t1)
    const ix1 = cx + rx_in  * Math.cos(t2), iy1 = cy + ry_in  * Math.sin(t2)
    return `M ${ox1} ${oy1} A ${rx_out} ${ry_out} 0 0 1 ${ox2} ${oy2} L ${ix1} ${iy1} A ${rx_in} ${ry_in} 0 0 0 ${ix2} ${iy2} Z`
  }

  // Taille emoji piège sur le circuit
  const piegeSize = kartSize * 0.85
  const piegesSet = new Set(pieges)

  return (
    <svg
      ref={svgRef}
      viewBox={animatedViewBox ?? computedViewBox ?? `0 0 ${W} ${H}`}
      className={`w-full h-full ${className}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <filter id={filterId} x="-60%" y="-60%" width="220%" height="220%">
          <feDropShadow dx="0" dy="4" stdDeviation="5" floodColor="rgba(0,0,0,0.65)" />
        </filter>
        <filter id="grayscale-kart" x="-60%" y="-60%" width="220%" height="220%">
          <feColorMatrix type="saturate" values="0" />
          <feDropShadow dx="0" dy="4" stdDeviation="5" floodColor="rgba(0,0,0,0.65)" />
        </filter>
      </defs>

      {/* Image de fond — suit automatiquement le pan du viewBox */}
      {imageUrl && (
        <image href={imageUrl} x="0" y="0" width={W} height={H} preserveAspectRatio="xMidYMid slice" />
      )}

      {/* Piste — donut */}
      <path d={`${outerPath} ${innerPath}`} fill="rgba(15,15,15,0.88)" fillRule="evenodd" />

      {/* Sections bonus dorées */}
      {cells.map((cell, i) => {
        if (!bonusIndices.has(i)) return null
        return (
          <path key={i}
            d={bonusSlicePath(cell.t)}
            fill="#fde047"
            fillOpacity={0.82}
          />
        )
      })}

      {/* Bordures */}
      <ellipse cx={cx} cy={cy} rx={rx_out} ry={ry_out}
        fill="none" stroke="white" strokeWidth="1.5" strokeOpacity="0.6" />
      <ellipse cx={cx} cy={cy} rx={rx_in} ry={ry_in}
        fill="none" stroke="white" strokeWidth="1.5" strokeOpacity="0.6" />

      {/* Damier départ/arrivée */}
      <g transform={`rotate(${startAngleDeg}, ${startX}, ${startY})`}>
        {[0, 1].flatMap(row =>
          Array.from({ length: sqCols }, (_, col) => (
            <rect
              key={`${row}-${col}`}
              x={startX - lineHalf + col * sqW}
              y={startY - bandH / 2 + row * sqH}
              width={sqW}
              height={sqH}
              fill={(row + col) % 2 === 0 ? 'white' : '#111'}
            />
          ))
        )}
      </g>

      {/* Pièges (bananes) sur le circuit — emoji brut, rendu natif */}
      {cells.map((cell, i) => {
        if (!piegesSet.has(i)) return null
        const px = cx + rx_c * Math.cos(cell.t)
        const py = cy + ry_c * Math.sin(cell.t)
        return (
          <text
            key={`piege-${i}`}
            x={px}
            y={py}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={piegeSize}
          >
            🍌
          </text>
        )
      })}

      {/* Karts fantômes — sur la ligne centrale de la piste (pas de floatUp) pour ne pas
          se superposer aux karts réels qui flottent au-dessus */}
      {ghostKarts.map(gk => {
        const pos     = (gk.position_case ?? 0) % nbCases
        const cell    = cells[pos]
        const animPos = animatedPositions[gk.id]
        const gx = animPos ? animPos.x : cell.x
        const gy = animPos ? animPos.y : cell.y  // pas de floatUp : fantôme sur la piste
        return (
          <g key={gk.id} opacity={0.55}>
            <image
              href={getTwemojiUrl(gk.type_vehicule)}
              x={gx - kartSize / 2}
              y={gy - kartSize / 2}
              width={kartSize}
              height={kartSize}
            />
          </g>
        )
      })}

      {/* Karts */}
      {kartElements.map(({ kart, kx, ky }) => {
        // Utiliser la position animée si disponible, sinon la position statique
        const animPos = animatedPositions[kart.id]
        const rx = animPos ? animPos.x        : kx
        const ry = animPos ? animPos.y - floatUp : ky

        const isMe      = myKartId != null && kart.id === myKartId
        const inactive  = !!kart.is_inactive
        const effEmoji  = !inactive && activeEffectEmoji(kart)
        const isHovered = hoveredKartId === kart.id

        const dispBubbleR  = isMe ? bubbleR : bubbleR * 0.82
        const dispKartSize = isMe ? kartSize : kartSize * 0.82

        return (
          <g key={kart.id}>
            {/* Groupe principal — opacité réduite si inactif, scale au survol */}
            <g
              opacity={inactive ? 0.55 : 1}
              onMouseEnter={() => setHoveredKartId(kart.id)}
              onMouseLeave={() => setHoveredKartId(null)}
              style={{
                transform: isHovered ? 'scale(1.15)' : undefined,
                transformOrigin: `${rx}px ${ry}px`,
                transition: 'transform 0.15s ease',
                cursor: 'pointer',
              }}
            >
              {/* Halo pulsant (joueur uniquement) */}
              {isMe && !inactive && (
                <circle cx={rx} cy={ry} r={bubbleR + 5} fill="none"
                  stroke="#E8601C" strokeWidth={1.5} opacity={0.6}>
                  <animate attributeName="opacity" values="0.6;0;0.6"
                    dur="1.5s" repeatCount="indefinite" />
                  <animate attributeName="r" values={`${bubbleR + 3};${bubbleR + 8};${bubbleR + 3}`}
                    dur="1.5s" repeatCount="indefinite" />
                </circle>
              )}
              {/* Halo de survol */}
              {isHovered && (
                <circle cx={rx} cy={ry} r={dispBubbleR + 4} fill="white" opacity={0.25} style={{ pointerEvents: 'none' }} />
              )}
              {/* Bulle principale */}
              <circle cx={rx} cy={ry} r={dispBubbleR}
                fill={inactive ? '#9CA3AF' : 'white'}
                stroke={isMe && !inactive ? '#E8601C' : 'none'}
                strokeWidth={isMe ? 2.5 : 0}
                filter={`url(#${inactive ? 'grayscale-kart' : filterId})`} />
              {/* Emoji véhicule */}
              <image
                href={getTwemojiUrl(kart.type_vehicule)}
                x={rx - dispKartSize / 2} y={ry - dispKartSize / 2}
                width={dispKartSize} height={dispKartSize}
              />
              {/* Effet actif en haut de la bulle */}
              {effEmoji && (
                <image
                  href={getTwemojiUrl(effEmoji)}
                  x={rx - 4} y={ry - dispBubbleR + 1}
                  width={8} height={8}
                />
              )}
            </g>
            {/* Badge 💤 hors du groupe opaque — toujours pleine opacité */}
            {inactive && (
              <text
                x={rx} y={ry - dispBubbleR + 2}
                textAnchor="middle" dominantBaseline="auto"
                fontSize={dispKartSize * 0.4}
                style={{ pointerEvents: 'none' }}
              >
                💤
              </text>
            )}
            {/* Tooltip pseudo au survol */}
            {isHovered && kart.pseudo && (
              <g style={{ pointerEvents: 'none' }}>
                <rect
                  x={rx - kart.pseudo.length * 2.6 - 5}
                  y={ry - dispBubbleR - 15}
                  width={kart.pseudo.length * 5.2 + 10}
                  height={11}
                  rx={3}
                  fill="rgba(0,0,0,0.82)"
                />
                <text
                  x={rx}
                  y={ry - dispBubbleR - 9.5}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={7}
                  fill="white"
                  fontFamily="system-ui, sans-serif"
                >
                  {kart.pseudo}
                </text>
              </g>
            )}
          </g>
        )
      })}

      {/* Pastilles "+n" (> 3 karts sur la même case) */}
      {kartBadges.map(({ n, bx, by }, i) => (
        <g key={`badge-${i}`}>
          <circle cx={bx} cy={by} r={bubbleR} fill="white" filter={`url(#${filterId})`} />
          <text
            x={bx} y={by}
            textAnchor="middle" dominantBaseline="central"
            fontSize={kartSize * 0.5} fontWeight="bold" fill="#1a1a1a"
          >
            +{n}
          </text>
        </g>
      ))}

      {/* Projectile d'attaque animé (prop legacy) */}
      {projectile && (() => {
        const px = projectile.fromX + (projectile.toX - projectile.fromX) * projectile.progress
        const py = projectile.fromY + (projectile.toY - projectile.fromY) * projectile.progress
        return svgEmoji(projectile.emoji, px, py - floatUp, kartSize * 1.5)
      })()}

      {/* Pseudo du joueur ciblé affiché sous sa bulle (prop legacy) */}
      {pseudoTarget && (
        <text
          x={pseudoTarget.x}
          y={pseudoTarget.y - floatUp + bubbleR + 10}
          textAnchor="middle"
          fontSize={9}
          fontWeight="bold"
          fill="white"
          stroke="black"
          strokeWidth="0.5"
          paintOrder="stroke"
        >
          {pseudoTarget.pseudo}
        </text>
      )}

      {/* Projectiles d'attaque animés (attackEvents) */}
      {activeProjectiles.map(p => (
        <image
          key={p.id}
          href={getTwemojiUrl(p.emoji)}
          x={p.x - kartSize * 1.35 / 2}
          y={p.y - floatUp - kartSize * 1.35 / 2}
          width={kartSize * 1.35}
          height={kartSize * 1.35}
        />
      ))}

      {/* Pseudo des karts ciblés par une attaque en cours */}
      {kartElements
        .filter(({ kart }) => targetedKartIds.has(kart.id))
        .map(({ kart, kx, ky }) => {
          const animPos = animatedPositions[kart.id]
          const rx = animPos ? animPos.x        : kx
          const ry = animPos ? animPos.y - floatUp : ky
          return (
            <text
              key={`target-${kart.id}`}
              x={rx}
              y={ry + bubbleR + 10}
              textAnchor="middle"
              fontSize={9}
              fontWeight="bold"
              fill="white"
              stroke="black"
              strokeWidth="0.5"
              paintOrder="stroke"
            >
              {kart.pseudo}
            </text>
          )
        })
      }
    </svg>
  )
}
